import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { MASTER_LIST } from "@/pages/masters/MasterDataLayout";
import {
  Users, Truck, Package, Boxes, Warehouse, Landmark, Coins, CircleDollarSign,
  ArrowLeftRight, FolderKanban, BookOpen, Calculator, Hash, Wallet, ChevronRight,
} from "lucide-react";

const ICONS = {
  customers: Users, suppliers: Truck, products: Package, product_categories: Boxes,
  units: Boxes, warehouses: Warehouse, bank_accounts: Landmark, cash_accounts: Coins,
  currencies: CircleDollarSign, exchange_rates: ArrowLeftRight, projects: FolderKanban,
  chart_of_accounts: BookOpen, taxes: Calculator, document_numbering: Hash, opening_balances: Wallet,
};

export default function MasterDataHub() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/masters/summary/all").then(({ data }) => setSummary(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const summaryMap = Object.fromEntries(summary.map((s) => [s.key, s]));

  const items = MASTER_LIST.filter((m) => summaryMap[m.key]);

  return (
    <div className="space-y-6" data-testid="master-hub-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Data Master</h1>
        <p className="text-slate-500 mt-2">Kelola seluruh data master sebagai fondasi transaksi keuangan, akuntansi, dan perpajakan.</p>
        {summaryMap.document_numbering && <button onClick={() => navigate("/data-master/format-nomor")} className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-700 underline underline-offset-4 hover:text-slate-900" data-testid="master-hub-numbering-link"><Hash className="w-4 h-4" />Format Nomor Transaksi (Jenis Dokumen & Singkatan Pihak)</button>}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Memuat data...</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Anda tidak memiliki hak akses untuk melihat modul Data Master.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((m) => {
            const Icon = ICONS[m.key] || Boxes;
            const stats = summaryMap[m.key] || { total: 0, active: 0 };
            return (
              <button
                key={m.key}
                onClick={() => navigate(`/data-master/${m.path}`)}
                className="text-left bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-500 hover:shadow-sm transition-colors group"
                data-testid={`master-card-${m.key}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center group-hover:bg-emerald-600 transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-emerald-600" />
                </div>
                <div className="font-display font-semibold text-base leading-tight">{m.label}</div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">{m.desc}</div>
                <div className="flex items-center gap-4 mt-4 text-xs">
                  <span className="text-slate-500">Total: <b className="font-mono-num text-slate-800">{stats.total}</b></span>
                  <span className="text-emerald-700">Aktif: <b className="font-mono-num">{stats.active}</b></span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
